import React from 'react';
import { Box, FormControlLabel, Switch, RadioGroup, Radio, Checkbox, FormGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CustomSettings } from 'subModule/src/services/invoice/types';

interface TableSettingsPanelProps {
  settings: CustomSettings;
  onChange: (settings: Partial<CustomSettings>) => void;
}

const TableSettingsPanel: React.FC<TableSettingsPanelProps> = ({ settings, onChange }) => {
  const { t } = useTranslation();

  const updateTableColumn = (column: string, value: boolean) => {
    if (settings.invoiceType === "consolidated") {
      onChange({
        consolidatedTableColumns: {
          ...settings.consolidatedTableColumns,
          [column]: value,
        },
      });
    } else {
      onChange({
        tableColumns: {
          ...settings.tableColumns,
          [column]: value,
        },
      });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={settings.showTable ?? true}
            onChange={(e) => onChange({ showTable: e.target.checked })}
            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4c0055' } }}
          />
        }
        label={t('showTable')}
        sx={{ m: 0 }}
      />

      {settings.showTable && (
        <>
          {settings.invoiceType === "consolidated" ? (
            /* Consolidated Invoice Table Columns */
            <Box>
              <label className="font-Saira font-[600] text-[13px] mb-2 block" style={{ color: '#333' }}>
                {t('consolidatedTableColumns') || 'Consolidated Table Columns'}
              </label>
              <FormGroup sx={{ mt: 1 }}>
                {[
                  { key: 'serialNo', label: t('serialNo') || 'Serial No' },
                  { key: 'vehicleName', label: t('vehicleName') || 'Vehicle Name' },
                  { key: 'vehiclePlateNo', label: t('vehiclePlateNo') || 'Vehicle Plate No' },
                  { key: 'deliveryNoteNo', label: t('deliveryNoteNo') || 'Delivery Note No' },
                  { key: 'subtotal', label: t('subtotal') || 'Sub Total' },
                  { key: 'discount', label: t('discount') || 'Discount' },
                  { key: 'discountedSubtotal', label: t('discountedSubtotal') || 'Discounted Subtotal' },
                  { key: 'tax', label: t('tax') || 'Tax' },
                  { key: 'total', label: t('total') || 'Total' },
                ].map(({ key, label }) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        size="small"
                        checked={settings.consolidatedTableColumns?.[key as keyof typeof settings.consolidatedTableColumns] ?? false}
                        onChange={(e) => updateTableColumn(key, e.target.checked)}
                        sx={{ '&.Mui-checked': { color: '#4c0055' } }}
                      />
                    }
                    label={label}
                    sx={{ m: 0 }}
                  />
                ))}
              </FormGroup>
            </Box>
          ) : (
            <>
              {/* Table Layout - Radio Buttons */}
              <Box>
                <label className="font-Saira font-[600] text-[13px] mb-2 block" style={{ color: '#333' }}>
                  {t('tableLayout')}
                </label>
                <RadioGroup
                  row
                  value={settings.tableLayout || 'combined'}
                  onChange={(e) => onChange({ tableLayout: e.target.value as any })}
                  sx={{ mt: 1 }}
                >
                  <FormControlLabel 
                    value="combined" 
                    control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#4c0055' } }} />} 
                    label={t('combined')} 
                  />
                  <FormControlLabel 
                    value="separate" 
                    control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#4c0055' } }} />} 
                    label={t('separate')} 
                  />
                </RadioGroup>
              </Box>

              {/* Show Sections */}
              <Box>
                <label className="font-Saira font-[600] text-[13px] mb-2 block" style={{ color: '#333' }}>
                  {t('showSections')}
                </label>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={settings.showPackages ?? true}
                        onChange={(e) => onChange({ showPackages: e.target.checked })}
                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4c0055' } }}
                      />
                    }
                    label={t('packages')}
                    sx={{ m: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={settings.showServices ?? true}
                        onChange={(e) => onChange({ showServices: e.target.checked })}
                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4c0055' } }}
                      />
                    }
                    label={t('services')}
                    sx={{ m: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={settings.showItems ?? true}
                        onChange={(e) => onChange({ showItems: e.target.checked })}
                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4c0055' } }}
                      />
                    }
                    label={t('items')}
                    sx={{ m: 0 }}
                  />
                </Box>
              </Box>

              {/* Item Display - Checkboxes */}
         

              {/* Table Columns */}
              <Box>
                <label className="font-Saira font-[600] text-[13px] mb-2 block" style={{ color: '#333' }}>
                  {t('tableColumns')}
                </label>

                
                <FormGroup sx={{ mt: 1 }}>
                  {['serialNo', 'item', 'quantity', 'unitPrice', 'amount', 'discount', 'discountedSubtotal', 'tax', 'totalWithTax'].map((column) => (
                    <FormControlLabel
                      key={column}
                      control={
                        <Checkbox
                          size="small"
                          checked={settings.tableColumns?.[column as keyof typeof settings.tableColumns] ?? false}
                          onChange={(e) => updateTableColumn(column, e.target.checked)}
                          sx={{ '&.Mui-checked': { color: '#4c0055' } }}
                        />
                      }
                      label={t(column)}
                      sx={{ m: 0 }}
                    />
                  ))}
                </FormGroup>
              </Box>
                   <Box>
                <label className="font-Saira font-[600] text-[13px] mb-2 block" style={{ color: '#333' }}>
                  {t('itemDisplay')}
                </label>
                <FormGroup sx={{ mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={settings.itemDisplayOptions?.showTitle ?? true}
                        onChange={(e) => onChange({
                          itemDisplayOptions: {
                            ...settings.itemDisplayOptions,
                            showTitle: e.target.checked,
                          }
                        })}
                        sx={{ '&.Mui-checked': { color: '#4c0055' } }}
                      />
                    }
                    label={t('title')}
                    sx={{ m: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={settings.itemDisplayOptions?.showCode ?? true}
                        onChange={(e) => onChange({
                          itemDisplayOptions: {
                            ...settings.itemDisplayOptions,
                            showCode: e.target.checked,
                          }
                        })}
                        sx={{ '&.Mui-checked': { color: '#4c0055' } }}
                      />
                    }
                    label={t('code')}
                    sx={{ m: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={settings.itemDisplayOptions?.showDescription ?? false}
                        onChange={(e) => onChange({
                          itemDisplayOptions: {
                            ...settings.itemDisplayOptions,
                            showDescription: e.target.checked,
                          }
                        })}
                        sx={{ '&.Mui-checked': { color: '#4c0055' } }}
                      />
                    }
                    label={t('description')}
                    sx={{ m: 0 }}
                  />
                </FormGroup>
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default TableSettingsPanel;
